import tinymce from "tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";

import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/code";

export function initTinyMCE() {
  document.addEventListener("DOMContentLoaded", () => {
    const textarea = document.querySelector("textarea.tinymce");
    if (!textarea) return;

    // --- Locale handling ---
    const tinymceLangMap = { de: "de", fr: "fr_FR", it: "it", en: "en" };
    const currentLocale = tinymceLangMap[window.APP_LOCALE] || "en";

    // --- Labels injected via Rails ---
    const labels = window.TINYMCE_LABELS || {
      insert_tableau: "Insert Tableau Button",
      attach_pdf: "Attach PDF",
      pdf_title: "Title",
      choose_file: "Choose PDF File",
      cancel: "Cancel",
      insert: "Insert",
      uploading: "Uploading PDF...",
      pdf_upload_failed: "PDF upload failed",
    };

    tinymce.init({
      license_key: "gpl",
      selector: "textarea.tinymce",
      skin_url: "/assets/tinymce/skins/ui/oxide",
      content_css: "/assets/tinymce/skins/content/default/content.css",
      language: currentLocale,
      language_url: `/assets/tinymce/langs/${currentLocale}.js`,

      plugins: "link image media table code",
      toolbar:
        "undo redo | styleselect | bold italic | alignleft aligncenter alignright | link image media pdfbutton | tableaubutton | code",
      height: 400,
      automatic_uploads: true,

      // --- Image upload handler ---
      images_upload_handler: function (blobInfo, progress) {
        return new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append("file", blobInfo.blob(), blobInfo.filename());

          fetch("/upload_image", {
            method: "POST",
            headers: {
              "X-CSRF-Token":
                document.querySelector("meta[name='csrf-token']")?.content || "",
            },
            body: formData,
          })
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              return response.json();
            })
            .then((json) => {
              if (json?.location && typeof json.location === "string") {
                resolve(json.location);
              } else {
                throw new Error("Invalid response JSON: missing `location` key");
              }
            })
            .catch((error) => {
              console.error("[TinyMCE Upload Error]", error);
              reject("Image upload failed");
            });
        });
      },

     extended_valid_elements:
        "a[href|target|rel|class|download|data-mce-fragment],\
        button[class|style|data-url|data-name],\
        div[class|id|style],\
        script[src|type],\
        img[src|style|alt|width|height|data-mce-fragment],\
        noscript,param[name|value],\
        object[class|width|height|style]",

      setup: function (editor) {
        // --- Tableau Button ---
        editor.ui.registry.addButton("tableaubutton", {
          text: labels.insert_tableau,
          icon: "embed",
          onAction: function () {
            editor.windowManager.open({
              title: labels.insert_tableau,
              body: {
                type: "panel",
                items: [
                  { type: "input", name: "url", label: "Tableau Public URL" },
                  { type: "input", name: "name", label: "Button Text" },
                  { type: "input", name: "dataname", label: "Data-Name Attribute (optional)" },
                ],
              },
              buttons: [
                { type: "cancel", text: labels.cancel || "Cancel" },
                { type: "submit", text: labels.insert || "Insert", primary: true },
              ],
              onSubmit: function (api) {
                const data = api.getData();
                const url = data.url?.trim();
                const label = data.name?.trim() || "Open Tableau";
                const dataname = data.dataname?.trim() || "Tableau Modal";

                if (url) {
                  editor.insertContent(`
                    <button class="btn btn-primary open-tableau-popup"
                            data-url="${url}"
                            data-name="${dataname}"
                            style="background-color:#4472c4; border-color:#4472c4;">
                      ${label}
                    </button>
                  `);
                  api.close();
                }
              },
            });
          },
        });

        // --- PDF Upload Button ---
        editor.ui.registry.addButton("pdfbutton", {
          text: labels.attach_pdf,
          icon: "new-document",
          onAction: function () {
            editor.windowManager.open({
              title: labels.attach_pdf,
              body: {
                type: "panel",
                items: [
                  {
                    type: "htmlpanel",
                    html: `<label style="display:block;font-weight:600;margin-bottom:4px;">${labels.pdf_title}</label>`,
                  },
                  { type: "input", name: "title", placeholder: labels.pdf_title },
                  {
                    type: "htmlpanel",
                    html: `<label style="display:block;font-weight:600;margin-top:12px;margin-bottom:4px;">${labels.choose_file}</label>
                           <input id="pdf-upload-input" type="file" accept="application/pdf" multiple style="margin-top:4px;">`,
                  },
                ],
              },
              buttons: [
                { type: "cancel", text: labels.cancel || "Cancel" },
                { type: "submit", text: labels.insert || "Insert", primary: true },
              ],
              onSubmit: async function (api) {
                const data = api.getData();
                const titleBase = data.title?.trim();
                const fileInput = document.getElementById("pdf-upload-input");
                const files = fileInput?.files;

                if (!files || !files.length) {
                  alert(labels.choose_file || "Please select a PDF file");
                  return;
                }

                editor.notificationManager.open({
                  text: labels.uploading || "Uploading PDF...",
                  type: "info",
                  timeout: 1000,
                });

                for (const file of files) {
                  const formData = new FormData();
                  formData.append("file", file);

                  try {
                    const response = await fetch("/upload_image", {
                      method: "POST",
                      headers: {
                        "X-CSRF-Token":
                          document.querySelector("meta[name='csrf-token']")?.content || "",
                      },
                      body: formData,
                    });

                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                    const json = await response.json();
                    if (json?.location) {
                      const finalTitle = titleBase || file.name;
                      editor.insertContent(
                        `<p><a href="${json.location}" download="${finalTitle}.pdf" target="_blank" rel="noopener noreferrer" class="pdf-link">
                            📎 ${finalTitle}
                          </a></p>`
                      );
                    }
                  } catch (error) {
                    console.error("[TinyMCE PDF Upload Error]", error);
                    alert(labels.pdf_upload_failed || "PDF upload failed");
                  }
                }

                api.close();
              },
            });
          },
        });
      },
    });
  });
}